/**
 * FirmaPortalKullanicilariSekmesi.jsx — Firma Detay › Portal Kullanıcıları (backlog #417, 2026-10-14; tasarım #409 D1 / D4)
 *
 * Bir firmanın GİRİŞLERİ ve her girişin bu firmadaki PORTAL ROLLERİ (chip'ler). Roller firmaya göre verilir: aynı giriş başka
 * bir firmada başka rolde olabilir. Rol atamayı / sonlandırmayı yalnızca SAKA personeli yapar (D4: firmanın kendi kullanıcı
 * yönetimi yok) ve yalnızca portal.kullanici_rol.manage sahibi (Admin; kök de geçer) — diğer personel listeyi SALT OKUNUR görür.
 * Giriş ↔ firma bağı Yetki Yönetimi › Firma–Kullanıcı'da da kurulur; buradaki "Kullanıcı ekle" (rbac.manage; kök geçer) aynı ucu
 * çağırır (KullaniciEkleDugmesi.jsx), böylece firmanın sayfasından çıkmadan bir giriş bağlanır. Profil & Belgeler › Kullanıcılar
 * (FirmaKullanicilarBolumu.jsx) aynı düğmeyi taşır.
 *
 * Bir girişte atanmış rol yoksa kaynak "varsayılan" olur: portal, o girişin user_group'unun varsayılan rolünü kullanır (PHP'nin
 * bağladığı bir giriş kilitli kalmaz). Rolleri SONLANDIRILMIŞ bir girişte bu varsayılan geri GELMEZ: hiç rol seçilmezse giriş
 * bu firmada her modülde NONE olur — pencere bunu açıkça söyler.
 *
 * Her satırda: "Neden?" (Yetki Açıklayıcı), "Önizle" (portalı bu girişin rolleriyle salt okunur aç), Geçmiş bağlantısı.
 *
 * #730 (2026-11-05): her (personel olmayan) satırda "Kullanıcıyı düzenle" — Kullanıcılar sayfasının / Kullanıcı Kartı'nın düzenleme penceresi
 * (UserFormModal, mode="edit") firmanın sayfasından çıkmadan açılır. Pencere tam kullanıcıyı ister (telefon, e-posta, ünvan, uyruk, durum…), tablonun
 * satırında bunlar yok; bu yüzden tıklanınca GET /users/{id}/kart (getKullaniciKarti) okunur. Düzenleme MANAGE_USER_ROLES ister (backend de denetler;
 * Kayıt / Saha yalnızca kullanici.olustur taşır): izni olmayana düğme çizilmez. Pencere kapanınca liste yeniden okunur.
 * Veri: services/portalRbacService.js → /portal-rbac/firma/{firmaId}/kullanicilar.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import {
  Card, CardBody, Table, Badge, Button, Input, Label, FormGroup, Alert, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getFirmaKullanicilari, setKullaniciRolleri, PORTAL_IZNI } from "../../../../services/portalRbacService";
import { PERMISSION_KEYS, useYetki } from "../../../../services/rbacService";
import { getKullaniciKarti } from "../../../../services/userService";
import { openOnizlemeYeniSekme } from "../../../../services/portalOnizleme";
import KullaniciEkleDugmesi from "./KullaniciEkleDugmesi";
import UserFormModal from "../../Users/UserFormModal";

const KAYNAK_RENK = { ATANMIS: "success", VARSAYILAN: "info", SONLANDIRILMIS: "warning", YOK: "secondary", PERSONEL: "dark" };

// #614: `bekleyenTalep` = this company's pending user addition requests (FirmaDetayPage reads it for the tab's badge; 0 for those who may not decide them).
export default function FirmaPortalKullanicilariSekmesi({ firmaId, fuarId, firmaName, bekleyenTalep = 0 }) {
  const { t, i18n } = useTranslation("ayarlar");
  // WAS (until #739): const navigate = useNavigate();  (the preview navigated this tab; it opens a new tab now)
  const duzenleyebilir = useYetki(PORTAL_IZNI.KULLANICI_ROL) === true;
  // #730: kullanıcının kendisini düzenlemek MANAGE_USER_ROLES ister (rol atamaktan ayrı bir izin).
  const kullaniciDuzenleyebilir = useYetki(PERMISSION_KEYS.MANAGE_USER_ROLES) === true;
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [duzenlenen, setDuzenlenen] = useState(null);   // satır
  const [secim, setSecim] = useState(new Set());
  const [kaydediyor, setKaydediyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [yenile, setYenile] = useState(0);
  // #730: düzenleme penceresine verilen tam kullanıcı (kartın okunmasını bekleyen satırın id'si `kartOkunan`da).
  const [kullaniciDuzenlenen, setKullaniciDuzenlenen] = useState(null);
  const [kartOkunan, setKartOkunan] = useState(null);
  const [kartHatasi, setKartHatasi] = useState(null);
  const [uyarilar, setUyarilar] = useState([]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect); yazmadan sonra yeniden okuma `yenile` sayacıyla.
  // Rol adları istek dilinde gelir: dil değişince yeniden okunur.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const data = await getFirmaKullanicilari(firmaId);
        if (!ignore) { setVeri(data); setHata(null); }
      } catch (e) {
        if (!ignore) setHata(e.message || t("portalKullanicilari.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaId, yenile, i18n.language]);

  const duzenle = (k) => {
    setIslemHatasi(null);
    setSecim(new Set(k.roller.map((r) => r.rolId)));
    setDuzenlenen(k);
  };

  const kaydet = async () => {
    setKaydediyor(true);
    setIslemHatasi(null);
    try {
      await setKullaniciRolleri(firmaId, duzenlenen.userId, [...secim]);
      setBilgi(t("portalKullanicilari.kaydedildi", { kullanici: duzenlenen.loginAdi }));
      setDuzenlenen(null);
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalKullanicilari.kaydedilemedi"));
    } finally {
      setKaydediyor(false);
    }
  };

  const kullaniciyiDuzenle = async (k) => {
    setKartHatasi(null);
    setKartOkunan(k.userId);
    try {
      const kart = await getKullaniciKarti(k.userId);
      setKullaniciDuzenlenen(kart.kullanici);
    } catch (e) {
      setKartHatasi(e.message || t("portalKullanicilari.kullaniciOkunamadi"));
    } finally {
      setKartOkunan(null);
    }
  };

  // sonuc = UserFormModal'ın kaydı (iptalde null): liste yeniden okunur; telefon / e-posta başka kullanıcılarda da varsa (#490) backend yine kaydeder, uyarılar gösterilir.
  const kullaniciDuzenlemeKapandi = (sonuc) => {
    const k = kullaniciDuzenlenen;
    setKullaniciDuzenlenen(null);
    if (!sonuc) return;
    setUyarilar(sonuc.uyarilar || []);
    setBilgi(t("portalKullanicilari.kullaniciGuncellendi", { kullanici: [k?.userName, k?.userSurname].filter(Boolean).join(" ") || k?.userLoginName }));
    setYenile((x) => x + 1);
  };

  // #739: the preview opens in a new tab. WAS: startOnizleme({ ... }); navigate("/Firma/Anasayfa"); in the same tab.
  const onizle = (k) => {
    openOnizlemeYeniSekme({ firmaId, firmaAdi: veri?.firmaAdi || firmaName, kullaniciId: k.userId, kullaniciAdi: k.loginAdi });
  };

  const rolAdi = (r) => r.ad;

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
          <div>
            <h5 className="mb-1">{t("portalKullanicilari.baslik")}</h5>
            <div className="text-muted small">{t("portalKullanicilari.aciklama")}</div>
          </div>
          <div className="d-flex gap-2">
            {/* Yetki Yönetimi'ndeki Firma–Kullanıcı ile aynı uç: rbac.manage (kök geçer); izni yoksa kapalı + neden. */}
            <KullaniciEkleDugmesi
              firmaId={firmaId} firmaAdi={veri?.firmaAdi || firmaName}
              onEklendi={(kullanici) => {
                setBilgi(t("portalKullanicilari.eklendi", { kullanici, firma: veri?.firmaAdi || firmaName }));
                setYenile((x) => x + 1);
              }}
            />
            <Link to="/Management/SistemAyarlari/Yetkiler?tab=gecmis" className="btn btn-light btn-sm" data-testid="portal-kullanici-gecmis">
              {t("portalKullanicilari.gecmis")}
            </Link>
          </div>
        </div>

        {bekleyenTalep > 0 && (
          <Alert color="warning" className="small d-flex align-items-center justify-content-between flex-wrap gap-2" data-testid="portal-kullanici-bekleyen-talep">
            <span>{t("yonetim:kullaniciTalepleri.bekleyenTalepler", { count: bekleyenTalep })}</span>
            <Link
              className="btn btn-warning btn-sm" data-testid="portal-kullanici-talepleri-ac"
              to={`/Management/Firmalar/KullaniciTalepleri?firmaId=${firmaId}&firmaAdi=${encodeURIComponent(veri?.firmaAdi || firmaName || "")}`}
            >
              {t("yonetim:kullaniciTalepleri.talepleriAc")}
            </Link>
          </Alert>
        )}
        {!duzenleyebilir && <Alert color="info" className="small" data-testid="portal-kullanici-salt">{t("portalKullanicilari.saltOkunur")}</Alert>}
        {hata && <Alert color="danger">{hata}</Alert>}
        {bilgi && <Alert color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        {kartHatasi && <Alert color="danger" toggle={() => setKartHatasi(null)} data-testid="portal-kullanici-kart-hata">{kartHatasi}</Alert>}
        {uyarilar.length > 0 && (
          <Alert color="warning" className="small" toggle={() => setUyarilar([])} data-testid="portal-kullanici-uyarilar">
            {uyarilar.map((u, i) => (
              <div key={u.kod ? `${u.kod}-${i}` : i} className={i > 0 ? "mt-2" : undefined}>
                <div>{u.mesaj}</div>
                {(u.kullanicilar || []).length > 0 && (
                  <div>
                    {u.kullanicilar.map((d, j) => (
                      <span key={d.userId}>
                        {j > 0 && ", "}
                        <Link to={`/Users/${d.userId}`} className="fw-semibold">{d.adSoyad || d.kullaniciAdi || `#${d.userId}`}</Link>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Alert>
        )}
        {!veri && !hata && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}

        {veri && (
          <div className="table-responsive">
            <Table className="align-middle" size="sm" data-testid="portal-kullanici-tablo">
              <thead className="table-light">
                <tr>
                  <th>{t("portalKullanicilari.giris")}</th>
                  <th>{t("portalKullanicilari.grup")}</th>
                  <th>{t("portalKullanicilari.roller")}</th>
                  <th>{t("portalKullanicilari.kaynak")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {veri.kullanicilar.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3">{t("portalKullanicilari.bos")}</td></tr>
                )}
                {veri.kullanicilar.map((k) => (
                  <tr key={k.userId} data-kullanici={k.loginAdi}>
                    <td>
                      <div className="fw-semibold">{k.loginAdi}</div>
                      <div className="small text-muted">{[k.ad, k.soyad].filter(Boolean).join(" ")}{!k.aktif && ` · ${t("portalKullanicilari.pasif")}`}</div>
                    </td>
                    <td className="small">{k.userGroupAdi}</td>
                    <td data-testid={`roller-${k.loginAdi}`}>
                      <div className="d-flex flex-wrap gap-1">
                        {k.roller.map((r) => <Badge color="primary" key={r.baglantiId} data-rol={r.kod}>{rolAdi(r)}</Badge>)}
                        {k.rolKaynagi === "VARSAYILAN" && k.varsayilanRol && (
                          <Badge color="light" className="text-dark border" data-rol={k.varsayilanRol.kod} title={t("portalKullanicilari.varsayilanIpucu")}>
                            {k.varsayilanRol.ad}
                          </Badge>
                        )}
                        {k.sonlandirilanRoller.map((r) => (
                          <Badge color="light" className="text-muted text-decoration-line-through" key={r.baglantiId}
                            title={t("portalKullanicilari.sonlandirildi", { tarih: (r.bitisTarihi || "").slice(0, 10), kim: r.bitirenAdi || "" })}>
                            {rolAdi(r)}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td><Badge color={KAYNAK_RENK[k.rolKaynagi] || "secondary"}>{t(`portalKullanicilari.kaynakAd.${k.rolKaynagi}`)}</Badge></td>
                    <td className="text-end text-nowrap">
                      {!k.personel && (
                        <>
                          <Link
                            className="btn btn-link btn-sm" data-testid={`neden-${k.loginAdi}`}
                            to={`/Management/SistemAyarlari/YetkiAciklayici?firmaId=${firmaId}&userId=${k.userId}&fuarId=${fuarId ?? ""}`}
                          >
                            {t("portalKullanicilari.neden")}
                          </Link>
                          <Button color="link" size="sm" onClick={() => onizle(k)} data-testid={`onizle-${k.loginAdi}`}>{t("portalKullanicilari.onizle")}</Button>
                          {kullaniciDuzenleyebilir && (
                            <Button
                              color="light" className="border me-1" size="sm" disabled={kartOkunan === k.userId}
                              onClick={() => kullaniciyiDuzenle(k)} data-testid={`kullanici-duzenle-${k.loginAdi}`}
                            >
                              {kartOkunan === k.userId ? <Spinner size="sm" className="me-1" /> : <Pencil size={13} className="me-1" />}
                              {t("portalKullanicilari.kullaniciyiDuzenle")}
                            </Button>
                          )}
                          {duzenleyebilir && (
                            <Button color="primary" outline size="sm" onClick={() => duzenle(k)} data-testid={`duzenle-${k.loginAdi}`}>
                              {t("portalKullanicilari.rolleriDuzenle")}
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      {kullaniciDuzenlenen && <UserFormModal mode="edit" user={kullaniciDuzenlenen} onClose={kullaniciDuzenlemeKapandi} />}

      <Modal isOpen={Boolean(duzenlenen)} toggle={() => setDuzenlenen(null)}>
        <ModalHeader toggle={() => setDuzenlenen(null)}>{t("portalKullanicilari.rolleriDuzenle")}: {duzenlenen?.loginAdi}</ModalHeader>
        <ModalBody>
          {islemHatasi && <Alert color="danger" className="small">{islemHatasi}</Alert>}
          <div className="text-muted small mb-2">{t("portalKullanicilari.rolSec", { firma: veri?.firmaAdi || firmaName })}</div>
          {(veri?.roller ?? []).map((r) => {
            const tutuyor = duzenlenen?.roller.some((x) => x.rolId === r.id);
            return (
              <FormGroup check key={r.id} className="mb-1">
                <Input
                  type="checkbox" id={`pr-${r.id}`} data-testid={`rol-sec-${r.kod}`} checked={secim.has(r.id)}
                  disabled={!r.aktif && !tutuyor}
                  onChange={(e) => setSecim((x) => { const y = new Set(x); if (e.target.checked) y.add(r.id); else y.delete(r.id); return y; })}
                />
                <Label check for={`pr-${r.id}`}>{r.ad}{!r.aktif && <span className="text-muted"> ({t("portalKullanicilari.pasif")})</span>}</Label>
              </FormGroup>
            );
          })}
          {secim.size === 0 && <Alert color="warning" className="small mt-3 mb-0" data-testid="portal-kullanici-hicrol">{t("portalKullanicilari.hicRolUyari")}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDuzenlenen(null)}>{t("portalKullanicilari.vazgec")}</Button>
          <Button color="primary" disabled={kaydediyor} onClick={kaydet} data-testid="portal-kullanici-kaydet">
            {kaydediyor ? <Spinner size="sm" /> : t("portalKullanicilari.kaydet")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
